import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, ShieldAlert, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fetchPublicShare } from "@/lib/projects";
import { fmtDays, fmtYears, fmtPct, fmtMoney } from "@/lib/chart-utils";

/**
 * Public (unauthenticated) viewer for a shared analysis.
 * Shows a read-only summary with the mandatory pedagogical disclaimer.
 */
export default function PublicShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPublicShare(token);
      setData(res);
    } catch (e) {
      setError(e?.message || "Lien invalide.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div
        className="min-h-screen bg-background px-4 py-10 text-center text-sm text-muted-foreground"
        data-testid="public-share-loading"
      >
        Chargement…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background px-4 py-10">
        <Card
          className="mx-auto max-w-xl border-rose-400/40 bg-rose-50 dark:bg-rose-950/40"
          data-testid="public-share-error"
        >
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-10 w-10 text-rose-600" />
            <h3 className="text-lg font-bold text-rose-900 dark:text-rose-100">
              Lien invalide ou expiré
            </h3>
            <p className="text-sm text-rose-800 dark:text-rose-200">
              {error}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const project = data?.project || {};
  const analysis = data?.analysis || {};
  const attrib = data?.attribution || {};
  const r = analysis.results || {};
  const currency = r.summary?.currency || "UM";
  const ind = r.profiles?.[0]?.individual || {};
  const debt = r.debt || {};
  const national = r.national || {};
  const legal = r.legal_analysis;

  return (
    <div
      className="min-h-screen bg-background px-4 py-8 sm:px-8"
      data-testid="public-share-page"
    >
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-brand-gold">
            <Sparkles className="h-3.5 w-3.5" />
            VITAE-PUBLICA™ · Rapport partagé
          </div>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
            {project.country || "—"} ·{" "}
            <span className="text-brand-gold">
              Exercice {project.fiscal_year || "—"}
            </span>
          </h1>
          <div className="mt-2 text-sm text-muted-foreground">
            {project.name}
            {attrib.organization && ` · ${attrib.organization}`}
            {attrib.name && ` · ${attrib.name}`}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Lien expire le :{" "}
            {data?.expires_at
              ? new Date(data.expires_at).toLocaleString("fr-FR")
              : "—"}
          </div>
        </div>

        {/* Disclaimer */}
        <Card
          className="border-2 border-rose-500/60 bg-rose-50 text-rose-950 dark:bg-rose-950/40 dark:text-rose-100"
          data-testid="public-share-disclaimer"
        >
          <CardContent className="flex gap-3 p-4 text-sm">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-rose-600 dark:text-rose-300" />
            <div>
              <div className="font-bold uppercase tracking-wide">
                ⚠️ Document pédagogique — Aucune valeur juridictionnelle
              </div>
              <p className="mt-1">
                Ce rapport partagé est un outil d'aide à la réflexion conforme
                à la Nouvelle Théorie des Finances Publiques (ELY Mustapha). Il
                ne constitue en aucun cas une décision de justice, un avis
                d'avocat, ou un acte juridique quelconque.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* KPI grid */}
        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="public-share-kpis"
        >
          <Kpi
            label="Jours/an sacrifiés"
            value={fmtDays(ind.jours_vie_sacrifies_par_an)}
            color="#C0392B"
          />
          <Kpi
            label="% vie fiscale"
            value={fmtPct(ind.ratio_vie_fiscale_pct)}
            color="#D97706"
          />
          <Kpi
            label="Années carrière"
            value={fmtYears(ind.annees_vie_sur_carriere)}
            color="#C9A84C"
          />
          <Kpi
            label="Années dette"
            value={fmtYears(debt.annees_vie_hypothequees)}
            color="#1A3C5E"
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Synthèse de l'analyse</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row
              label="Profil analysé"
              value={r.profiles?.[0]?.profile_name || "—"}
            />
            <Row
              label="Salaire mensuel"
              value={fmtMoney(ind.salaire_mensuel, currency)}
            />
            <Row
              label="Total prélèvements / an"
              value={fmtMoney(ind.total_prelevements, currency)}
            />
            <Row
              label="Budget national (années cumulées)"
              value={fmtYears(national.annees_vies_budget)}
            />
            <Row
              label="Dette par contribuable"
              value={fmtMoney(debt.dette_par_contribuable, currency)}
            />
          </CardContent>
        </Card>

        {/* Legal snippets (if any) */}
        {legal?.infractions?.length > 0 && (
          <Card data-testid="public-share-legal">
            <CardHeader>
              <CardTitle className="text-base">
                Infractions identifiées{" "}
                <Badge variant="outline" className="ml-2">
                  {legal.infractions.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {legal.infractions.slice(0, 10).map((inf, i) => (
                  <li
                    key={i}
                    className="rounded-md border border-border bg-muted/30 p-2"
                  >
                    <div className="font-semibold">{inf.qualification}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      Montant : {fmtMoney(inf.montant_um, currency)} · Jours
                      de vie : {fmtDays(inf.jours_de_vie)} · Gravité niveau{" "}
                      {inf.niveau_gravite}
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <div className="pt-4 text-center text-xs text-muted-foreground">
          VITAE-PUBLICA™ — Les Finances Publiques sont des Tranches de Vies ·
          <em> Ahmed ELY Mustapha</em>.
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div
      className="rounded-lg border p-3"
      style={{ borderColor: `${color}50`, backgroundColor: `${color}08` }}
    >
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div
        className="mt-1 font-mono text-2xl font-bold"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 pb-2 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-semibold">{value}</span>
    </div>
  );
}
